import { lazy } from "react";
import moment from "moment";
import { loadCalendarLib } from "@/extension/utils/lazy-lib";

/**
 * React.lazy boundary over the react-big-calendar LIB LAYER.
 *
 * The lib (Calendar + withDragAndDrop + momentLocalizer) is fetched on first
 * render from the lazy/calendar.js WAR bundle, which shares app.js's React via
 * the __BT_REACT__ bridge (see src/extension/utils/lazy-lib.ts). We compose
 * `withDragAndDrop(Calendar)` and bind the moment localizer ONCE at resolve time
 * — mirroring the previous module-scope construction — then forward every prop.
 *
 * `localizer` is supplied here (the caller no longer passes it), because
 * momentLocalizer now lives in the lazy bundle. moment is the shared app.js
 * singleton, so runtime `moment.updateLocale(...)` calls elsewhere still reflect.
 *
 * Must be rendered inside a <Suspense> boundary (calendar/calendar-view.tsx
 * provides one with an overlay-sized skeleton fallback).
 */
const LazyDnDCalendar = lazy(async () => {
  const { Calendar, momentLocalizer, withDragAndDrop } = await loadCalendarLib();
  const localizer = momentLocalizer(moment);
  const DnDCalendar = withDragAndDrop(Calendar);
  const Wrapped = (props: any) => <DnDCalendar localizer={localizer} {...props} />;
  return { default: Wrapped };
});

export default LazyDnDCalendar;

/**
 * Overlay-sized placeholder shown while lazy/calendar.js loads (one-time, then
 * cached). Fills the calendar container so the surrounding chrome (header/FAB/
 * dialogs, all still in app.js) doesn't reflow. Kept intentionally lightweight.
 */
export const CalendarSkeleton = () => (
  <div
    style={{
      width: "100%",
      height: "100%",
      minHeight: 320,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--calendar-bg)",
      color: "var(--text-secondary, #667781)",
      fontSize: 14,
      opacity: 0.7,
    }}
    aria-busy="true"
    aria-live="polite"
  >
    <span className="rbc-toolbar-label" style={{ animation: "btPulse 1.2s ease-in-out infinite" }}>
      Loading calendar…
    </span>
    <style>{`@keyframes btPulse { 0%,100% { opacity: .45 } 50% { opacity: .9 } }`}</style>
  </div>
);
